/**
 * Auto-answer: the phone picks up inbound calls without the rep clicking Answer.
 *
 * What it is for. A setter on a headset works a list for hours; a click between every call is
 * both friction and a dropped call when they mis-aim. It also completes the conference-room
 * flow — "Create room" rings the rep's own phone, and with auto-answer on they are simply *in*
 * the room a second later instead of clicking Answer on their own call.
 *
 * Per browser, not per account. Auto-answer describes the seat, not the person: the same rep
 * wants it on at the desk with a headset and off on a laptop in a meeting, and a setting that
 * followed them to the laptop would have their machine start talking to a lead out loud. So it
 * lives in `localStorage` (origin-scoped, survives reloads) rather than in `ui_prefs` on the
 * server.
 */

const STORAGE_KEY = "acl.softphone.autoAnswer";

export function readAutoAnswer(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // Private mode and similar. Off is the safe default: a phone that answers by itself when
    // the rep did not ask it to is worse than one that needs a click.
    return false;
  }
}

export function writeAutoAnswer(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    /* non-fatal — the setting simply will not survive a reload */
  }
}

/** The shape this decision needs, so it can be tested without a Verto client or React. */
export interface AutoAnswerCall {
  id: string;
  direction: "inbound" | "outbound";
  state: string;
}

/**
 * Whether the phone should pick `incomingCallId` up on its own.
 *
 * The rule that is not obvious: **never while any other call exists**. Verto does not put the
 * first call on hold when a second is answered, so auto-answering alongside another leg drops
 * the rep into two live calls at once — the lead they were talking to hears the new one. A rep
 * who genuinely wants to take a second call chooses hold-&-answer, which is unaffected by this.
 *
 * "Any other call", not "any other *answered* call, deliberately. A call is only marked `active`
 * after `getUserMedia`, the SDP exchange and the awaited `verto.answer` round trip; until then
 * it reads as `ringing` or `trying`. Two calls inside that window would both have passed a
 * check for active-or-held, and both would have been answered with nothing held.
 *
 * `calls` is the client's own snapshot and already contains the incoming call — and never
 * contains an ended one, because the vendored client deletes a call from its map before it
 * emits, so "another call exists" cannot be satisfied by a call that has already finished.
 */
export function shouldAutoAnswer(
  enabled: boolean,
  incomingCallId: string,
  calls: readonly AutoAnswerCall[],
): boolean {
  if (!enabled) return false;
  const incoming = calls.find((c) => c.id === incomingCallId);
  if (!incoming || incoming.direction !== "inbound" || incoming.state !== "ringing") return false;
  return !calls.some((c) => c.id !== incomingCallId);
}

/**
 * The call id out of an `incomingCall` event.
 *
 * The vendored client emits **one object** per event (`emit('incomingCall', callInfo)`), not
 * positional arguments — the same trap `remoteStreamFrom` exists for. Getting it wrong here is
 * quiet in the worst way: auto-answer would simply never fire, and nobody would know whether
 * the setting or the switch was at fault.
 */
export function incomingCallIdFrom(args: unknown[]): string | null {
  const payload = args[0];
  if (!payload || typeof payload !== "object") return null;
  const callId = (payload as { callId?: unknown }).callId;
  return typeof callId === "string" && callId ? callId : null;
}
